import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button, Chip, Drawer, FormField, Input, Select } from '@/ui';
import { useDebouncedValue } from '@/lib/task-server-list';
import {
  EMPTY_FILTERS,
  activeFilterCount,
  type ListFilters,
} from '@/lib/task-list-url';
import { CROSS_PAIR_CODE, DISPLAYED_CURRENCY_CODES } from '@/lib/rate-mapping';
import styles from './ListToolbar.module.css';

/**
 * ARAMA + FILTRE + SIRALAMA.
 *
 * Durumun tamami YUKARIDA (adres cubugunda) yasiyor; bu bilesen yalniz onu
 * cizip degisiklik bildiriyor. Tek istisna arama kutusunun taslagi: her tus
 * vurusunda URL'ye yazmak, gecmis yiginini kirk kayitla doldururdu.
 */

/**
 * Filtre panelindeki para birimleri. Disari acilmiyor: `react-refresh` bir
 * bilesen dosyasinin yalniz bilesen disa aktarmasini istiyor ve bu sabitin
 * baska bir tuketicisi de yok.
 */
const CURRENCY_OPTIONS: readonly string[] = [
  ...DISPLAYED_CURRENCY_CODES,
  CROSS_PAIR_CODE,
];

export type SortOption = { value: string; labelKey: string };

export type ListToolbarProps = {
  filters: ListFilters;
  onChange: (next: ListFilters) => void;
  sortOptions: readonly SortOption[];
};

export function ListToolbar({ filters, onChange, sortOptions }: ListToolbarProps) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * Arama taslagi. `base` yazma ANINDAKI sunucu degeri; disaridan bir degisiklik
   * gelirse (filtreler temizlendi) `base` bayatlar ve taslak DUSER. Bu, efekt
   * icinde setState cagirmadan "disaridan gelen deger kazanir" davranisi verir —
   * efektte state yazmak render sirasini gorunmez bicimde degistirir.
   */
  const [draft, setDraft] = useState({ base: filters.q, text: filters.q });
  const text = draft.base === filters.q ? draft.text : filters.q;
  const debounced = useDebouncedValue(text, 250);

  useEffect(() => {
    // Iki kosul da SART:
    //  - gecikme oturdu (debounced === text): yazarken istek atilmaz;
    //  - deger gercekten farkli: "filtreleri temizle"den hemen sonra bayat bir
    //    gecikmeli deger geri yazilmasin.
    if (debounced !== text) return;
    if (debounced === filters.q) return;
    onChange({ ...filters, q: debounced });
  }, [debounced, text, filters, onChange]);

  const count = activeFilterCount(filters);

  const removeChip = (patch: Partial<ListFilters>): void => {
    onChange({ ...filters, ...patch });
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.row}>
        <Input
          className={styles.search}
          type="search"
          value={text}
          placeholder={t('filter.search')}
          aria-label={t('filter.search')}
          onChange={(event) => {
            setDraft({ base: filters.q, text: event.target.value });
          }}
        />
        <Button
          tone="neutral"
          iconLeft={<SlidersHorizontal size={16} aria-hidden="true" />}
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          {count > 0 ? `${t('filter.title')} (${String(count)})` : t('filter.title')}
        </Button>
      </div>

      {count > 0 ? (
        <div className={styles.chips}>
          {filters.currencies.map((code) => (
            <FilterChip
              key={code}
              label={code}
              onRemove={() => {
                removeChip({ currencies: filters.currencies.filter((item) => item !== code) });
              }}
            />
          ))}
          {filters.createdFrom ? (
            <FilterChip
              label={`${t('filter.dateFrom')}: ${filters.createdFrom}`}
              onRemove={() => {
                removeChip({ createdFrom: '' });
              }}
            />
          ) : null}
          {filters.createdTo ? (
            <FilterChip
              label={`${t('filter.dateTo')}: ${filters.createdTo}`}
              onRemove={() => {
                removeChip({ createdTo: '' });
              }}
            />
          ) : null}
          {filters.amountMin ? (
            <FilterChip
              label={`${t('filter.amountMin')}: ${filters.amountMin}`}
              onRemove={() => {
                removeChip({ amountMin: '' });
              }}
            />
          ) : null}
          {filters.amountMax ? (
            <FilterChip
              label={`${t('filter.amountMax')}: ${filters.amountMax}`}
              onRemove={() => {
                removeChip({ amountMax: '' });
              }}
            />
          ) : null}
          <Button
            tone="ghost"
            size="sm"
            onClick={() => {
              // Siralama KORUNUR: siralama bir filtre degil, listenin okunma bicimi.
              onChange({ ...EMPTY_FILTERS, sort: filters.sort });
            }}
          >
            {t('filter.clearAll')}
          </Button>
        </div>
      ) : null}

      <Drawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        title={t('filter.title')}
      >
        {/* Drawer kapaliyken cocuklarini HIC baglamiyor; bu yuzden asagidaki
            form her acilista taze bir taslakla baslar ve "acilista taslagi
            senkronla" diye bir efekte gerek kalmaz. */}
        <FilterForm
          filters={filters}
          sortOptions={sortOptions}
          onApply={(next) => {
            onChange(next);
            setDrawerOpen(false);
          }}
          onReset={() => {
            onChange({ ...EMPTY_FILTERS });
            setDrawerOpen(false);
          }}
        />
      </Drawer>
    </div>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <Chip tone="primary" size="sm">
      {label}
      <button type="button" className={styles.chipRemove} onClick={onRemove} aria-label={t('common.close')}>
        <X size={12} aria-hidden="true" />
      </button>
    </Chip>
  );
}

type FilterFormProps = {
  filters: ListFilters;
  sortOptions: readonly SortOption[];
  onApply: (next: ListFilters) => void;
  onReset: () => void;
};

function FilterForm({ filters, sortOptions, onApply, onReset }: FilterFormProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<ListFilters>(filters);

  const toggleCurrency = (code: string): void => {
    setValue((current) => ({
      ...current,
      currencies: current.currencies.includes(code)
        ? current.currencies.filter((item) => item !== code)
        : [...current.currencies, code],
    }));
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        onApply(value);
      }}
    >
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{t('filter.currency')}</legend>
        <div className={styles.currencies}>
          {CURRENCY_OPTIONS.map((code) => {
            const selected = value.currencies.includes(code);
            return (
              <button
                key={code}
                type="button"
                className={[styles.currency, selected ? styles.currencySelected : undefined]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={selected}
                onClick={() => {
                  toggleCurrency(code);
                }}
              >
                {code}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className={styles.pair}>
        <FormField label={t('filter.dateFrom')}>
          <Input
            type="date"
            value={value.createdFrom}
            onChange={(event) => {
              setValue((current) => ({ ...current, createdFrom: event.target.value }));
            }}
          />
        </FormField>
        <FormField label={t('filter.dateTo')}>
          <Input
            type="date"
            value={value.createdTo}
            onChange={(event) => {
              setValue((current) => ({ ...current, createdTo: event.target.value }));
            }}
          />
        </FormField>
      </div>

      <div className={styles.pair}>
        <FormField label={t('filter.amountMin')}>
          <Input
            numeric
            inputMode="decimal"
            value={value.amountMin}
            onChange={(event) => {
              setValue((current) => ({ ...current, amountMin: event.target.value }));
            }}
          />
        </FormField>
        <FormField label={t('filter.amountMax')}>
          <Input
            numeric
            inputMode="decimal"
            value={value.amountMax}
            onChange={(event) => {
              setValue((current) => ({ ...current, amountMax: event.target.value }));
            }}
          />
        </FormField>
      </div>

      <FormField label={t('filter.sort')}>
        <Select
          value={value.sort}
          placeholder={t('task.sortDefault')}
          options={sortOptions.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
          onChange={(event) => {
            setValue((current) => ({ ...current, sort: event.target.value }));
          }}
        />
      </FormField>

      <div className={styles.formActions}>
        <Button tone="ghost" onClick={onReset}>
          {t('filter.clearAll')}
        </Button>
        <Button type="submit">{t('filter.apply')}</Button>
      </div>
    </form>
  );
}
